// React.
import React, { Fragment } from 'react';
import { useHistory } from 'react-router-dom';

// Material UI
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import VisibilityIcon from '@material-ui/icons/Visibility';

// Components.
import Pagination from '@material-ui/lab/Pagination';

// Redux
import { useSelector, useDispatch } from "react-redux";
import { RootStore } from "../../../Store";
import { preorderEventsSetFilters } from '../../../actions/preorderEventActions/PreorderEventActions';

// Moment for date formatting
import Moment from 'moment';

// Translations.
import { useTranslation } from 'react-i18next';

// Styles.
import useStyles from './PreorderEventAccordionStyles';

const PreorderEventAccordion = () => {
  const classes = useStyles();
  const [t] = useTranslation('global');
  const dispatch = useDispatch();
  const history = useHistory();

  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { preorderEventList, preorderEventFilters } = preorderEventState;

  const currentPage = preorderEventFilters?.page || 0;

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    const newFilters = {
      ...preorderEventFilters,
      page: page - 1,
    };
    dispatch(preorderEventsSetFilters(newFilters));
  };

  const handleViewDetails = (preorderId: number) => {
    history.push(`/admin/preorder-event-details?preorderId=${preorderId}`);
  };

  if (!preorderEventList || preorderEventList.content.length === 0) {
    return (
      <div className={classes.noResults}>
        <Typography variant="h6" color="textSecondary">
          {t('preorderEvents.no-preorder-events')}
        </Typography>
      </div>
    );
  }

  return (
    <Fragment>
      {preorderEventList.content.map((preorderEvent, index) => (
        <Accordion key={preorderEvent.id} className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${index}-content`}
            id={`panel-${index}-header`}
            className={classes.accordionSummary}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="h6" className={classes.eventName}>
                  {preorderEvent.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Chip
                  label={preorderEvent.archived === true ? t('common.archived') : preorderEvent.closed === true ? t('common.closed') : t('common.active')}
                  color={preorderEvent.archived === true ? 'default' : preorderEvent.closed === true ? 'secondary' : 'primary'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">
                  {t('preorderEvents.items-count')}: {preorderEvent.itemsCount || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="body2" color="textSecondary">
                  {Moment(preorderEvent.releaseDate).format('DD/MM/YYYY')}
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" className={classes.label}>
                      {t('preorderEvents.release-date')}
                    </Typography>
                    <Typography variant="body2" className={classes.value}>
                      {Moment(preorderEvent.releaseDate).format('DD/MM/YYYY')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" className={classes.label}>
                      {t('preorderEvents.last-preorder-date')}
                    </Typography>
                    <Typography variant="body2" className={classes.value}>
                      {Moment(preorderEvent.lastPreorderDate).format('DD/MM/YYYY')}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" className={classes.label}>
                      {t('common.status')}
                    </Typography>
                    <Typography variant="body2" className={classes.value}>
                      {preorderEvent.archived === true ? t('common.archived') : preorderEvent.closed === true ? t('common.closed') : t('common.active')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" className={classes.label}>
                      {t('preorderEvents.items-count')}
                    </Typography>
                    <Typography variant="body2" className={classes.value}>
                      {preorderEvent.itemsCount || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetails(preorderEvent.id)}
                      size="small"
                    >
                      {t('common.view-details')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
      
      {preorderEventList?.totalPages !== undefined && preorderEventList?.totalPages > 1 && (
        <div className={classes.paginationContainer}>
          <Pagination 
            count={preorderEventList.totalPages}
            page={currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </div>
      )}
    </Fragment>
  );
};

export default PreorderEventAccordion;